import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import Header from './Header';

const {
  FiUser, FiLock, FiSave, FiArrowLeft, FiCheck, FiAlertCircle, FiEdit3, FiX, FiExternalLink, FiCopy,
  FiImage, FiVideo, FiMusic, FiFile
} = FiIcons;

function ClientProfile() {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: user.name,
    username: user.username,
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [activeTab, setActiveTab] = useState('profile');
  const [selectedFile, setSelectedFile] = useState(null);
  const [editingFile, setEditingFile] = useState(null);
  const [fileMetadata, setFileMetadata] = useState({});
  const [copiedUrl, setCopiedUrl] = useState(null);

  // Mock user files - In production, this would come from API
  const [userFiles, setUserFiles] = useState([
    {
      id: 1,
      name: 'project-proposal.pdf',
      originalName: 'project-proposal.pdf',
      url: 'https://example.com/file1.pdf',
      size: 2048576,
      type: 'application/pdf',
      uploadedAt: new Date(Date.now() - 86400000),
      metadata: {
        title: 'Project Proposal Document',
        description: 'Q4 2024 project proposal with budget estimates',
        tags: ['proposal', 'q4-2024', 'budget'],
        alt: 'Project proposal document with financial details'
      }
    },
    {
      id: 2,
      name: 'banner-image.jpg',
      originalName: 'banner-image.jpg',
      url: 'https://picsum.photos/seed/banner1/400/300.jpg',
      size: 1536000,
      type: 'image/jpeg',
      uploadedAt: new Date(Date.now() - 172800000),
      metadata: {
        title: 'Website Banner',
        description: 'Main banner image for homepage',
        tags: ['banner', 'homepage', 'marketing'],
        alt: 'Colorful website banner with company branding'
      }
    },
    {
      id: 3,
      name: 'presentation.mp4',
      originalName: 'presentation.mp4',
      url: 'https://example.com/file3.mp4',
      size: 15728640,
      type: 'video/mp4',
      uploadedAt: new Date(Date.now() - 259200000),
      metadata: {
        title: 'Product Presentation',
        description: 'Product features and benefits presentation',
        tags: ['presentation', 'product', 'demo'],
        alt: 'Video presentation showcasing product features'
      }
    },
    {
      id: 4,
      name: 'team-photo.jpg',
      originalName: 'team-photo.jpg',
      url: 'https://picsum.photos/seed/team1/400/300.jpg',
      size: 896000,
      type: 'image/jpeg',
      uploadedAt: new Date(Date.now() - 345600000),
      metadata: {
        title: 'Team Photo',
        description: 'Annual team building event photo',
        tags: ['team', 'event', 'culture'],
        alt: 'Team members gathered at company event'
      }
    }
  ]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    if (formData.newPassword && formData.newPassword !== formData.confirmPassword) {
      setMessage({ type: 'error', text: 'New passwords do not match' });
      setLoading(false);
      return;
    }

    if (formData.newPassword && formData.newPassword.length < 6) {
      setMessage({ type: 'error', text: 'Password must be at least 6 characters' });
      setLoading(false);
      return;
    }

    try {
      const updates = {
        name: formData.name,
        username: formData.username
      };

      if (formData.newPassword) {
        updates.password = formData.newPassword;
      }

      updateUser(user.id, updates);
      
      setMessage({ 
        type: 'success', 
        text: 'Profile updated successfully!' 
      });

      setFormData(prev => ({
        ...prev,
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      }));

    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: 'Failed to update profile. Please try again.' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/dashboard');
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (type) => {
    const mainType = type.split('/')[0];
    switch (mainType) {
      case 'image': return FiImage;
      case 'video': return FiVideo;
      case 'audio': return FiMusic;
      default: return FiFile;
    }
  };

  const copyToClipboard = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedUrl(url);
      setTimeout(() => setCopiedUrl(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleFileEdit = (file) => {
    setEditingFile(file);
    setFileMetadata({
      name: file.name,
      title: file.metadata?.title || '',
      description: file.metadata?.description || '',
      tags: file.metadata?.tags?.join(', ') || '',
      alt: file.metadata?.alt || ''
    });
  };

  const handleFileSave = () => {
    if (!editingFile) return;

    setUserFiles(prev => prev.map(file => 
      file.id === editingFile.id 
        ? {
            ...file,
            name: fileMetadata.name,
            metadata: {
              title: fileMetadata.title,
              description: fileMetadata.description,
              tags: fileMetadata.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
              alt: fileMetadata.alt
            }
          }
        : file
    ));

    setEditingFile(null);
    setFileMetadata({});
  };

  const isImageFile = (type) => type.startsWith('image/');

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center space-x-4">
            <motion.button 
              whileHover={{ scale: 1.05 }} 
              whileTap={{ scale: 0.95 }}
              onClick={handleCancel}
              className="p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-white transition-colors"
            >
              <SafeIcon icon={FiArrowLeft} className="w-5 h-5" />
            </motion.button>
            <h1 className="text-3xl font-bold text-gray-900">My Profile</h1>
          </div>
        </motion.div>

        {/* Tabs */}
        <div className="mb-8">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8">
              <button
                onClick={() => setActiveTab('profile')}
                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'profile'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Profile Settings
              </button>
              <button
                onClick={() => setActiveTab('gallery')}
                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'gallery'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                File Gallery ({userFiles.length})
              </button>
            </nav>
          </div>
        </div>

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="grid grid-cols-1 lg:grid-cols-3 gap-8"
          >
            {/* Profile Info Card */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="text-center">
                  <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <SafeIcon icon={FiUser} className="w-10 h-10 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{user.name}</h3>
                  <p className="text-gray-500 mb-1">{user.username}</p>
                  <p className="text-sm text-gray-400 capitalize">{user.role}</p>
                  
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <div className="space-y-3 text-left">
                      <div>
                        <p className="text-xs text-gray-500">S3 Folder</p>
                        <p className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">{user.s3Folder}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Total Files</p>
                        <p className="text-lg font-bold text-gray-900">{userFiles.length}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Permissions</p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {user.permissions.map(perm => (
                            <span key={perm} className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                              {perm}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Edit Form */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Edit Profile</h2>
                
                {message.text && (
                  <motion.div 
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    className={`mb-6 p-4 rounded-lg flex items-center space-x-3 ${
                      message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                    }`}
                  >
                    <SafeIcon icon={message.type === 'success' ? FiCheck : FiAlertCircle} className="w-5 h-5" />
                    <span>{message.text}</span>
                  </motion.div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Full Name
                        </label>
                        <input
                          type="text"
                          value={formData.name}
                          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Username
                        </label>
                        <input
                          type="text"
                          value={formData.username}
                          onChange={(e) => setFormData(prev => ({ ...prev, username: e.target.value }))}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Change Password</h3>
                    <p className="text-sm text-gray-500 mb-4">Leave blank if you don't want to change your password</p>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          New Password
                        </label>
                        <input
                          type="password"
                          value={formData.newPassword}
                          onChange={(e) => setFormData(prev => ({ ...prev, newPassword: e.target.value }))}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                          placeholder="Enter new password"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Confirm New Password
                        </label>
                        <input
                          type="password"
                          value={formData.confirmPassword}
                          onChange={(e) => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                          placeholder="Confirm new password"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-4 pt-6 border-t border-gray-200">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      type="submit"
                      disabled={loading}
                      className="flex items-center space-x-2 bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                      <SafeIcon icon={FiSave} className="w-4 h-4" />
                      <span>{loading ? 'Saving...' : 'Save Changes'}</span>
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      type="button"
                      onClick={handleCancel}
                      className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-all"
                    >
                      Cancel
                    </motion.button>
                  </div>
                </form>
              </div>
            </div>
          </motion.div>
        )}

        {/* Gallery Tab */}
        {activeTab === 'gallery' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">My Files</h2>
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <span>{userFiles.length} files</span>
                  <span>•</span>
                  <span>{formatFileSize(userFiles.reduce((acc, file) => acc + file.size, 0))}</span>
                </div>
              </div>

              {userFiles.length === 0 ? (
                <div className="text-center py-12">
                  <SafeIcon icon={FiFile} className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No files uploaded</h3>
                  <p className="text-gray-500">Start uploading files to see them here</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {userFiles.map((file) => (
                    <motion.div
                      key={file.id}
                      whileHover={{ y: -4 }}
                      className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-all cursor-pointer group"
                      onClick={() => setSelectedFile(file)}
                    >
                      {/* Thumbnail */}
                      <div className="aspect-video bg-gray-100 relative overflow-hidden">
                        {isImageFile(file.type) ? (
                          <img
                            src={file.url}
                            alt={file.metadata?.alt || file.name}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                            loading="lazy"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <SafeIcon icon={getFileIcon(file.type)} className="w-12 h-12 text-gray-400" />
                          </div>
                        )}
                        
                        {/* Overlay */}
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all flex items-center justify-center">
                          <SafeIcon icon={FiEdit3} className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      </div>

                      {/* File Info */}
                      <div className="p-4">
                        <h3 className="font-medium text-gray-900 truncate mb-1" title={file.name}>
                          {file.name}
                        </h3>
                        <p className="text-xs text-gray-500 mb-2">
                          {formatFileSize(file.size)} • {formatDistanceToNow(new Date(file.uploadedAt), { addSuffix: true })}
                        </p>
                        
                        {/* Tags */}
                        {file.metadata?.tags && file.metadata.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {file.metadata.tags.slice(0, 2).map((tag, index) => (
                              <span key={index} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                                {tag}
                              </span>
                            ))}
                            {file.metadata.tags.length > 2 && (
                              <span className="text-xs text-gray-400">+{file.metadata.tags.length - 2}</span>
                            )}
                          </div>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* File Detail Modal */}
        <AnimatePresence>
          {selectedFile && !editingFile && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
              onClick={() => setSelectedFile(null)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
                  <h3 className="text-xl font-bold text-gray-900">{selectedFile.name}</h3>
                  <button
                    onClick={() => setSelectedFile(null)}
                    className="p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <SafeIcon icon={FiX} className="w-5 h-5" />
                  </button>
                </div>

                <div className="p-6">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Preview */}
                    <div>
                      <h4 className="text-lg font-medium text-gray-900 mb-4">Preview</h4>
                      <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden">
                        {isImageFile(selectedFile.type) ? (
                          <img
                            src={selectedFile.url}
                            alt={selectedFile.metadata?.alt || selectedFile.name}
                            className="w-full h-full object-contain"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <SafeIcon icon={getFileIcon(selectedFile.type)} className="w-16 h-16 text-gray-400" />
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Details */}
                    <div>
                      <h4 className="text-lg font-medium text-gray-900 mb-4">File Details</h4>
                      <div className="space-y-4">
                        <div>
                          <label className="text-sm text-gray-500">File Name</label>
                          <p className="font-medium text-gray-900">{selectedFile.name}</p>
                        </div>
                        <div>
                          <label className="text-sm text-gray-500">File Size</label>
                          <p className="font-medium text-gray-900">{formatFileSize(selectedFile.size)}</p>
                        </div>
                        <div>
                          <label className="text-sm text-gray-500">File Type</label>
                          <p className="font-medium text-gray-900">{selectedFile.type}</p>
                        </div>
                        <div>
                          <label className="text-sm text-gray-500">Uploaded</label>
                          <p className="font-medium text-gray-900">
                            {formatDistanceToNow(new Date(selectedFile.uploadedAt), { addSuffix: true })}
                          </p>
                        </div>
                        <div>
                          <label className="text-sm text-gray-500">URL</label>
                          <div className="flex items-center space-x-2 mt-1">
                            <input
                              type="text"
                              value={selectedFile.url}
                              readOnly
                              className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm font-mono"
                            />
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => copyToClipboard(selectedFile.url)}
                              className="p-2 text-gray-500 hover:text-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
                            >
                              <SafeIcon icon={copiedUrl === selectedFile.url ? FiCheck : FiCopy} className="w-4 h-4" />
                            </motion.button>
                            <motion.a
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              href={selectedFile.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 text-gray-500 hover:text-green-600 rounded-lg hover:bg-green-50 transition-colors"
                            >
                              <SafeIcon icon={FiExternalLink} className="w-4 h-4" />
                            </motion.a>
                          </div>
                        </div>

                        {/* SEO Metadata */}
                        {selectedFile.metadata && (
                          <div>
                            <label className="text-sm text-gray-500">SEO Metadata</label>
                            <div className="mt-2 space-y-2">
                              {selectedFile.metadata.title && (
                                <div>
                                  <p className="text-xs text-gray-400">Title</p>
                                  <p className="text-sm text-gray-900">{selectedFile.metadata.title}</p>
                                </div>
                              )}
                              {selectedFile.metadata.description && (
                                <div>
                                  <p className="text-xs text-gray-400">Description</p>
                                  <p className="text-sm text-gray-900">{selectedFile.metadata.description}</p>
                                </div>
                              )}
                              {selectedFile.metadata.alt && (
                                <div>
                                  <p className="text-xs text-gray-400">Alt Text</p>
                                  <p className="text-sm text-gray-900">{selectedFile.metadata.alt}</p>
                                </div>
                              )}
                              {selectedFile.metadata.tags && selectedFile.metadata.tags.length > 0 && (
                                <div>
                                  <p className="text-xs text-gray-400">Tags</p>
                                  <div className="flex flex-wrap gap-1 mt-1">
                                    {selectedFile.metadata.tags.map((tag, index) => (
                                      <span key={index} className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                        {tag}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        <div className="pt-4 border-t border-gray-200">
                          <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => handleFileEdit(selectedFile)}
                            className="w-full flex items-center justify-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors"
                          >
                            <SafeIcon icon={FiEdit3} className="w-4 h-4" />
                            <span>Edit File Details</span>
                          </motion.button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Edit File Modal */}
        <AnimatePresence>
          {editingFile && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
              onClick={() => setEditingFile(null)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
                  <h3 className="text-xl font-bold text-gray-900">Edit File Details</h3>
                  <button
                    onClick={() => setEditingFile(null)}
                    className="p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <SafeIcon icon={FiX} className="w-5 h-5" />
                  </button>
                </div>

                <div className="p-6">
                  <form onSubmit={(e) => { e.preventDefault(); handleFileSave(); }} className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        File Name
                      </label>
                      <input
                        type="text"
                        value={fileMetadata.name}
                        onChange={(e) => setFileMetadata(prev => ({ ...prev, name: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        SEO Title
                      </label>
                      <input
                        type="text"
                        value={fileMetadata.title}
                        onChange={(e) => setFileMetadata(prev => ({ ...prev, title: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Enter SEO title"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Description
                      </label>
                      <textarea
                        value={fileMetadata.description}
                        onChange={(e) => setFileMetadata(prev => ({ ...prev, description: e.target.value }))}
                        rows={3}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Enter file description"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Alt Text (for images)
                      </label>
                      <textarea
                        value={fileMetadata.alt}
                        onChange={(e) => setFileMetadata(prev => ({ ...prev, alt: e.target.value }))}
                        rows={2}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Describe the image for accessibility"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Tags
                      </label>
                      <input
                        type="text"
                        value={fileMetadata.tags}
                        onChange={(e) => setFileMetadata(prev => ({ ...prev, tags: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Enter tags separated by commas"
                      />
                      <p className="text-xs text-gray-500 mt-1">Separate multiple tags with commas</p>
                    </div>

                    <div className="flex items-center space-x-4 pt-6 border-t border-gray-200">
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        type="submit"
                        className="flex items-center space-x-2 bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
                      >
                        <SafeIcon icon={FiSave} className="w-4 h-4" />
                        <span>Save Changes</span>
                      </motion.button>
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        type="button"
                        onClick={() => setEditingFile(null)}
                        className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                      >
                        Cancel
                      </motion.button>
                    </div>
                  </form>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

export default ClientProfile;